import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { BiLogoPlayStore } from 'react-icons/bi';
import { FaApple } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ConfirmationModal } from '../../../shared/components/modal/ConfirmationModal';
import usePagination from '../../../shared/hooks/usePagination';
import { IAllStates } from '../../../store/rootReducer';
import { gtmService } from '../../../utils/gtm.service';
import { ErrorToast } from '../../../utils/toasters';
import BillingInfoModal from '../../shared/user/components/BillingInfoModal';
import { IMyCampaignsFilterProps } from '../campaign/dto/IMyCampaigns';
import DesktopDashboard from './DesktopDashboard';
import { IMyOffers } from './dto/IDashboard';
import MobileDashboard from './MobileDashboard';
import { InfluencerDashboardApi } from './service';
import { ICampaignOverview } from '../../../model/briefing/overview/ICampaignOverview';
import { IOfferWithCampaign } from '../profile/profile-view';
import { useMediaQuery } from '@mui/material';
import { BootstrapBreakpoints } from '../../../enum/BootstrapBreakpoints';
import AlertBox from '../../../shared/components/alert/AlertBox';
import { useNavigate } from 'react-router-dom';
import { UserRoles } from '../../../enum/UserRoles';
import { DEFAULT_DOCUMENT_TITLE } from '../../../utils/constants';
import { StoreKeeper } from '../../../store/store';
import { loadingSlice } from '../../../store/general/loadingSlice';

export function InfluencerDashboard() {
    StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        user: { completionInfo, userType, hasDevice, agencyCompany, id },
    } = useSelector((reduxState: IAllStates) => reduxState.auth);
    const [offers, setOffers] = useState<IOfferWithCampaign[]>([]);
    const [isBillingModalOpened, setIsBillingModalOpened] = useState(false);
    const [isWarningOpened, setIsWarningOpened] = useState(false);
    const { totalPages, setTotalPages, filterParams, handlePages, setFilterParams } = usePagination({ perPage: 10 });
    const [myOffers, setMyOffers] = useState<IMyOffers>();
    const [contractCampaigns, setContractCampaigns] = useState<ICampaignOverview[]>([]);
    const appsModalOpenedTime = Number(localStorage.getItem('appsModalOpenedTime') || 0);
    const isMobile = useMediaQuery(`(max-width: ${BootstrapBreakpoints.MD}px)`);
    const [showPopup, setShowPopup] = useState(false);

    const [isSummaryLoading, setIsSummaryLoading] = useState(true);
    const [isContractsLoading, setIsContractsLoading] = useState(true);
    const [isOffersLoading, setIsOffersLoading] = useState(true);

    useEffect(() => {
        if (!hasDevice && userType === UserRoles.INFLUENCER) {
            const diffTime = moment(new Date())?.diff(appsModalOpenedTime, 'day');
            if (diffTime > 7) {
                // open modal every 7 days || if appsModalOpenedTime is 0, it will enter here because 0 is Jan 01 1970
                setShowPopup(true);
                localStorage.setItem('appsModalOpenedTime', `${new Date().getTime()}`);
            }
        }
    }, [hasDevice, userType, appsModalOpenedTime]);

    // Gornje kartice (summary) i long-term ugovori
    useEffect(() => {
        InfluencerDashboardApi.getSummary()
            .then(response => {
                setMyOffers(response.myOffers);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsSummaryLoading(false));

        InfluencerDashboardApi.getContracts()
            .then(response => {
                setContractCampaigns(response?.data);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsContractsLoading(false));
    }, []);

    useEffect(() => {
        if (!(filterParams as IMyCampaignsFilterProps)?.sortType) {
            setFilterParams((prev: any) => ({ ...prev, sortType: 'DESC', sortBy: 'date' }));
        }
    }, []);

    useEffect(() => {
        if (!completionInfo?.billingInfo && !agencyCompany?.id) {
            setIsWarningOpened(true);
        }
    }, [agencyCompany, completionInfo?.billingInfo]);

    useEffect(() => {
        if (!(filterParams as IMyCampaignsFilterProps)?.sortType) {
            return;
        }

        setIsOffersLoading(true);
        let isCurrent = true;
        InfluencerDashboardApi.getOffers({
            ...filterParams,
            perPage: 9,
        })
            .then(response => {
                if (!isCurrent) return;
                setTotalPages(Math.ceil(response.count / response.perPage));
                setOffers(response.data);
                gtmService('search');
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsOffersLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [filterParams]);

    const dashboardProps = {
        contractCampaigns,
        filterParams,
        setFilterParams,
        handlePages,
        totalPages,
        myOffers: myOffers as IMyOffers,
        offers,
        isInvoiceModalOpen: isBillingModalOpened,
        setIsInvoiceModalOpen: setIsBillingModalOpened,
        isSummaryLoading,
        isContractsLoading,
        isOffersLoading,
    };

    return (
        <>
            <Helmet>
                <title>{DEFAULT_DOCUMENT_TITLE}</title>
            </Helmet>
            <BillingInfoModal
                show={isBillingModalOpened}
                closeModal={() => setIsBillingModalOpened(false)}
                userType={userType}
            />
            <ConfirmationModal
                description={t('general.updateBillingModal')}
                showSuccessButton={!completionInfo?.billingInfo}
                show={isWarningOpened}
                iconUrl={`/icons/profile/${!completionInfo?.billingInfo ? 'online-shopping' : 'user'}.png`}
                action={() => {
                    setIsWarningOpened(false);
                    setIsBillingModalOpened(true);
                }}
                closeModal={() => setIsWarningOpened(false)}
            />
            {!completionInfo?.profileInfo && (
                <AlertBox
                    image="/icons/errorProfile.svg"
                    classCol="mb-4 px-0"
                    variant="danger"
                    title={t('influencer.dashboard.errorProfile')}
                    desc={t('influencer.dashboard.errorProfileDesc')}
                    textButton={t('general.editProfile')}
                    functionBtn={() => navigate(`/settings/edit/${id}`)}
                />
            )}
            <MobileDashboard {...dashboardProps} />
            <DesktopDashboard {...dashboardProps} />

            <Modal centered show={showPopup} size="lg" onHide={() => setShowPopup(false)}>
                <div className="position-relative">
                    <img
                        src={`/icons/mobile/${isMobile ? 'mobile-' : ''}website-popup.png`}
                        alt="Website popup to download apps"
                        className="w-100"
                    />
                    <div className="d-flex align-items-center store-btn-wrapper">
                        <a
                            className="store-button"
                            href="https://apps.apple.com/tr/app/influence-vision/id6474192561"
                            target={'_blank'}
                            rel={'noreferrer'}
                        >
                            <FaApple className="text-white" size={30} />
                            <div className="d-flex flex-column ml-1">
                                <span className={'text-uppercase font-10'}>Download on the</span>
                                <span className="font-16">App store</span>
                            </div>
                        </a>
                        <a
                            className="store-button"
                            href="https://play.google.com/store/apps/details?id=com.influencevision.app&hl=en_US"
                            target={'_blank'}
                            rel={'noreferrer'}
                        >
                            <BiLogoPlayStore className="text-white" size={30} />
                            <div className="d-flex flex-column ml-1">
                                <span className={'text-uppercase font-10'}>Get it on</span>
                                <span className="font-16">Google Play</span>
                            </div>
                        </a>
                    </div>
                </div>
                <Modal.Footer className={'border-top p-0'}>
                    <Button variant={'outline-primary'} onClick={() => setShowPopup(false)}>
                        close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
